import React, { useState } from "react";

const BACKDROPS = [
  "Gold",
  "Rose Gold",
  "Silver",
  "Black",
  "White",
  "Champagne",
  "White Flower Wall -- $49",
  "Grass Hedge Wall -- $49",
];

export default function ContactUs() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    venueAddress: "",
    eventTime: "",
    stripStyle: "",
    designTheme: "",
    wordings: "",
    backdrop: "",
  });

  const [status, setStatus] = useState("idle"); // idle | sending | success | error
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setErrorMsg("");

    try {
      const res = await fetch("https://pixlybooth.com/contact.php", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });

      const data = await res.json();

      if (data.success) {
        setStatus("success");
        setForm({
          name: "",
          email: "",
          phone: "",
          venueAddress: "",
          eventTime: "",
          stripStyle: "",
          designTheme: "",
          wordings: "",
          backdrop: "",
        });
      } else {
        setStatus("error");
        setErrorMsg(data.message || "Something went wrong. Please try again.");
      }
    } catch {
      setStatus("error");
      setErrorMsg("Network error. Please try again later.");
    }
  };

  /* ── shared input classes ── */
  const inputCls =
    "w-full rounded-xl bg-[#0B0F1F] border border-white/10 px-4 py-3 text-white placeholder:text-gray-500 focus:outline-none focus:border-[#E34364] focus:ring-1 focus:ring-[#E34364]/40 transition-all duration-300";
  const labelCls = "block text-sm font-medium text-gray-300 mb-1.5";

  return (
    <section className="relative min-h-screen pt-28 pb-20 bg-gradient-to-b from-[#05030D] via-[#070A18] to-[#05030D] text-white overflow-hidden">
      {/* ── decorative blurs ── */}
      <div className="pointer-events-none absolute -top-40 -left-40 w-[500px] h-[500px] rounded-full bg-[#E34364]/10 blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-40 -right-40 w-[500px] h-[500px] rounded-full bg-[#9F5CFF]/10 blur-[140px]" />

      <div className="relative max-w-4xl mx-auto px-4 md:px-8">
        {/* ── heading ── */}
        <div className="text-center mb-12 animate-fade-up">
          <p className="text-[11px] uppercase tracking-[0.35em] text-pink-300/80 mb-3">
            Let's Create Something Beautiful
          </p>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold bg-gradient-to-r from-white via-pink-100 to-[#E34364] bg-clip-text text-transparent leading-tight">
            Request a Quote
          </h1>
          <p className="mt-4 text-sm md:text-base text-pink-100/70 max-w-lg mx-auto">
            Fill in the details below and our team will get back to you within 24 hours
            with availability and pricing.
          </p>
        </div>

        {/* ── card with gradient border ── */}
        <div className="rounded-[28px] p-[1px] bg-gradient-to-br from-[#E34364] via-[#9F5CFF] to-[#3B1D7A] shadow-[0_30px_80px_rgba(0,0,0,0.85)] animate-fade-up">
          <div className="rounded-[26px] bg-[#060719]/95 backdrop-blur-xl px-6 py-10 md:px-10 md:py-12">
            <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">

              {/* ─── SECTION LABEL ─── */}
              <div className="md:col-span-2 mb-1">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <span className="inline-block w-1.5 h-5 rounded-full bg-gradient-to-b from-[#E34364] to-[#9F5CFF]" />
                  Your Details
                </h2>
              </div>

              {/* Customer Name */}
              <div>
                <label htmlFor="contact-name" className={labelCls}>Customer Name *</label>
                <input
                  id="contact-name"
                  name="name"
                  value={form.name}
                  onChange={handleChange}
                  required
                  className={inputCls}
                  placeholder="Enter your full name"
                />
              </div>

              {/* Customer Email */}
              <div>
                <label htmlFor="contact-email" className={labelCls}>Customer Email *</label>
                <input
                  id="contact-email"
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  required
                  className={inputCls}
                  placeholder="you@email.com"
                />
              </div>

              {/* Customer Phone */}
              <div>
                <label htmlFor="contact-phone" className={labelCls}>Customer Phone *</label>
                <input
                  id="contact-phone"
                  type="tel"
                  name="phone"
                  value={form.phone}
                  onChange={handleChange}
                  required
                  className={inputCls}
                  placeholder="Best number to reach you"
                />
              </div>

              {/* Event Venue Address */}
              <div>
                <label htmlFor="contact-venue" className={labelCls}>Event Venue Address</label>
                <input
                  id="contact-venue"
                  name="venueAddress"
                  value={form.venueAddress}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder="Venue name & address"
                />
              </div>

              {/* Event Time */}
              <div>
                <label htmlFor="contact-time" className={labelCls}>Event Time</label>
                <input
                  id="contact-time"
                  type="datetime-local"
                  name="eventTime"
                  value={form.eventTime}
                  onChange={handleChange}
                  className={`${inputCls} [color-scheme:dark]`}
                />
              </div>

              {/* Spacer on desktop */}
              <div className="hidden md:block" />

              {/* ─── SEPARATOR ─── */}
              <div className="md:col-span-2 my-2">
                <div className="h-px bg-gradient-to-r from-transparent via-[#E34364]/30 to-transparent" />
              </div>

              {/* ─── PHOTO STRIP SECTION ─── */}
              <div className="md:col-span-2 mb-1">
                <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                  <span className="inline-block w-1.5 h-5 rounded-full bg-gradient-to-b from-[#E34364] to-[#9F5CFF]" />
                  Photo Strip Preferences
                </h2>
              </div>

              {/* Photo Strip Style — radio buttons */}
              <div className="md:col-span-2">
                <p className={labelCls}>Photo Strip Style *</p>
                <div className="flex flex-wrap gap-4 mt-1">
                  {[
                    { value: "2x6", label: "2×6 (Vertical)" },
                    { value: "4x6", label: "4×6 (Horizontal)" },
                  ].map(({ value, label }) => (
                    <label
                      key={value}
                      className={`relative flex items-center gap-3 cursor-pointer rounded-xl border px-5 py-3.5 transition-all duration-300 ${
                        form.stripStyle === value
                          ? "border-[#E34364] bg-[#E34364]/10 shadow-[0_0_20px_rgba(227,67,100,0.15)]"
                          : "border-white/10 bg-[#0B0F1F] hover:border-white/25"
                      }`}
                    >
                      <input
                        type="radio"
                        name="stripStyle"
                        value={value}
                        checked={form.stripStyle === value}
                        onChange={handleChange}
                        required
                        className="sr-only"
                      />
                      {/* custom radio dot */}
                      <span
                        className={`flex items-center justify-center w-5 h-5 rounded-full border-2 transition-all duration-300 ${
                          form.stripStyle === value
                            ? "border-[#E34364]"
                            : "border-gray-500"
                        }`}
                      >
                        {form.stripStyle === value && (
                          <span className="block w-2.5 h-2.5 rounded-full bg-[#E34364] animate-[scale-in_0.2s_ease]" />
                        )}
                      </span>
                      <span className="text-sm text-white font-medium">{label}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Strip Design Theme */}
              <div>
                <label htmlFor="contact-theme" className={labelCls}>Strip Design Theme</label>
                <input
                  id="contact-theme"
                  name="designTheme"
                  value={form.designTheme}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder="e.g. Floral, Minimalist, Retro…"
                />
              </div>

              {/* Backdrop */}
              <div>
                <label htmlFor="contact-backdrop" className={labelCls}>Backdrop *</label>
                <select
                  id="contact-backdrop"
                  name="backdrop"
                  value={form.backdrop}
                  onChange={handleChange}
                  required
                  className={inputCls}
                >
                  <option value="">Select a backdrop</option>
                  {BACKDROPS.map((b) => (
                    <option key={b} value={b}>{b}</option>
                  ))}
                </select>
              </div>

              {/* Wordings on Photo Strip */}
              <div className="md:col-span-2">
                <label htmlFor="contact-wordings" className={labelCls}>Wordings on Photo Strip</label>
                <textarea
                  id="contact-wordings"
                  name="wordings"
                  value={form.wordings}
                  onChange={handleChange}
                  rows={3}
                  className={`${inputCls} resize-none`}
                  placeholder="Any text you'd like printed on the photo strip…"
                />
              </div>

              {/* ─── SEPARATOR ─── */}
              <div className="md:col-span-2 my-1">
                <div className="h-px bg-gradient-to-r from-transparent via-[#E34364]/30 to-transparent" />
              </div>

              {/* ─── SUBMIT ─── */}
              <div className="md:col-span-2 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mt-2">
                <button
                  type="submit"
                  disabled={status === "sending"}
                  className="group relative inline-flex items-center justify-center gap-2 rounded-full bg-gradient-to-r from-[#E34364] to-[#c72e52] text-white font-semibold text-sm px-8 py-3 shadow-[0_8px_30px_rgba(227,67,100,0.35)] hover:shadow-[0_12px_40px_rgba(227,67,100,0.5)] hover:-translate-y-[1px] active:translate-y-0 transition-all duration-300 disabled:opacity-60 disabled:pointer-events-none"
                >
                  {status === "sending" ? (
                    <>
                      <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                      </svg>
                      Sending…
                    </>
                  ) : (
                    "Submit Request"
                  )}
                </button>
                <p className="text-[11px] text-pink-100/60">
                  By submitting, you agree to be contacted about your booking inquiry.
                </p>
              </div>

              {/* ─── STATUS MESSAGES ─── */}
              {status === "success" && (
                <div className="md:col-span-2 mt-2 rounded-xl bg-emerald-500/10 border border-emerald-400/30 px-5 py-3 text-emerald-300 text-sm flex items-center gap-3 animate-fade-up">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                  Thank you! Your request has been sent. We'll contact you shortly.
                </div>
              )}

              {status === "error" && (
                <div className="md:col-span-2 mt-2 rounded-xl bg-red-500/10 border border-red-400/30 px-5 py-3 text-red-300 text-sm flex items-center gap-3 animate-fade-up">
                  <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  {errorMsg}
                </div>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
